/**
 * Pints & Pegs Variant Manager
 * Manages drinking space configurations, bonus peg detection,
 * randomized space assignments, and modal pause/resume orchestration.
 */

export const PINTS_OPTIONS_DEFS = Object.freeze([
  {
    key: 'youDrink',
    chkId: 'optYouDrink',
    name: 'You Drink',
    icon: '🍺',
    description: 'Take a drink when you land on this space',
    actionText: 'Landing here requires the player to take a drink',
    color: '#f59e0b',
    defaultHoles: [15, 45, 75, 105]
  },
  {
    key: 'guysDrink',
    chkId: 'optGuysDrink',
    name: 'Guys Drink',
    icon: '🍻',
    description: 'All male players take a drink',
    actionText: 'Landing here requires all guys to take a drink',
    color: '#6366f1',
    defaultHoles: [12, 42, 72, 102]
  },
  {
    key: 'galsDrink',
    chkId: 'optGalsDrink',
    name: 'Gals Drink',
    icon: '🍷',
    description: 'All female players take a drink',
    actionText: 'Landing here requires all gals to take a drink',
    color: '#f43f5e',
    defaultHoles: [18, 48, 78, 108]
  },
  {
    key: 'leftDrink',
    chkId: 'optLeftDrink',
    name: 'Player to the left Drinks',
    icon: '👈',
    description: 'Person to your left takes a drink',
    actionText: 'Landing here requires the player to your left to take a drink',
    color: '#3b82f6',
    defaultHoles: [35, 65, 95, 115]
  },
  {
    key: 'rightDrink',
    chkId: 'optRightDrink',
    name: 'Player to the right Drinks',
    icon: '👉',
    description: 'Person to your right takes a drink',
    actionText: 'Landing here requires the player to your right to take a drink',
    color: '#06b6d4',
    defaultHoles: [5, 35, 65, 95]
  },
  {
    key: 'everyoneDrink',
    chkId: 'optEveryoneDrink',
    name: 'Everyone Drinks',
    icon: '🥂',
    description: 'Social! Every player at the table takes a drink',
    actionText: 'Landing here requires everyone playing to take a drink',
    color: '#10b981',
    defaultHoles: [30, 60, 90, 120]
  },
  {
    key: 'everyoneButYouDrink',
    chkId: 'optEveryoneButYouDrink',
    name: 'Everyone but you Drinks',
    icon: '🎉',
    description: 'Everyone except the pegging player takes a drink',
    actionText: 'Landing here requires everyone except you to take a drink',
    color: '#8b5cf6',
    defaultHoles: [20, 50, 80, 110]
  },
  {
    key: 'pickWhoDrinks',
    chkId: 'optPickWhoDrinks',
    name: 'You pick who drinks',
    icon: '🎯',
    description: 'Choose any single opponent or player to take a drink',
    actionText: 'Landing here allows you to choose who takes a drink',
    color: '#ec4899',
    defaultHoles: [10, 40, 70, 100]
  }
]);

export class PintsManager {
  constructor(options = {}) {
    this.enabled = Boolean(options.enabled);
    this.options = { ...options };
    this.activeHoles = new Map(); // holeNum -> optionDef
    this.rebuildHoleMap();
  }

  rebuildHoleMap(settings = this.options) {
    this.activeHoles.clear();
    for (const def of PINTS_OPTIONS_DEFS) {
      if (settings && (settings[def.key] || settings[def.chkId] || settings[`opt${def.name.replace(/\s+/g, '')}`])) {
        const holes = settings[`${def.key}Holes`] || def.defaultHoles;
        for (const h of holes) {
          if (h > 0) this.activeHoles.set(h, def);
        }
      }
    }
  }

  checkHole(holeNum) {
    return this.activeHoles.get(holeNum) || null;
  }

  /**
   * Retrieves all active pints space configurations based on settings and options definitions.
   * @param {Object} settings Current game settings object
   * @param {Array} [optionsList] List of pints option definitions (defaults to PINTS_OPTIONS_DEFS)
   * @returns {Array} List of active configs { id, hole, start, end, pegs, type, item }
   */
  getAllActiveConfigs(settings, optionsList = PINTS_OPTIONS_DEFS) {
    if (!settings) return [];
    const all = [];
    for (const opt of optionsList) {
      const isChecked = Boolean(settings[opt.chkId] || settings[opt.key]);
      if (!isChecked) continue;

      if (typeof opt.getConfig === 'function') {
        const list = opt.getConfig();
        if (Array.isArray(list) && list.length > 0) {
          list.forEach(c => all.push({ ...c, item: opt }));
          continue;
        }
      }

      const count = Math.min(4, Math.max(1, parseInt(settings[opt.key + 'Count'], 10) || (opt.defaultHoles ? opt.defaultHoles.length : 1)));
      const pegs = Math.min(5, Math.max(1, parseInt(settings[opt.key + 'Pegs'], 10) || 1));
      const holes = settings[opt.key + 'Holes'] || opt.defaultHoles || [15];
      for (let i = 0; i < count; i++) {
        const h = holes[i] != null ? holes[i] : (opt.defaultHoles ? opt.defaultHoles[i] : 15);
        all.push({
          id: i + 1,
          hole: h,
          start: h,
          end: h + pegs - 1,
          pegs: pegs,
          type: opt.key,
          label: `${opt.name} ${i + 1}`,
          item: opt
        });
      }
    }
    return all;
  }

  /**
   * Resolves whether a specific board hole triggers an active Pints drinking space.
   * @param {number} hole Hole number (1-121 or 1-481)
   * @param {Object} settings Current game settings
   * @param {Array} [optionsList]
   * @param {string} [brand='pints'] Current application brand
   * @returns {Object|null}
   */
  getActiveOptionAtHole(hole, settings, optionsList = PINTS_OPTIONS_DEFS, brand = 'pints') {
    if (!settings) return null;
    const list = optionsList || PINTS_OPTIONS_DEFS;
    const hasAnyOption = list.some(o => settings[o.chkId] || settings[o.key]);
    if (brand !== 'pints' && !hasAnyOption) return null;
    const all = this.getAllActiveConfigs(settings, list);
    return all.find(c => hole >= c.start && hole <= c.end) || null;
  }

  /**
   * Randomizes Pints spaces across the board without overlapping.
   * @param {Object} settings Current game settings
   * @param {boolean} [isFull=false] Whether to enable all options (full) or a subset
   * @param {Array} [optionsList]
   * @returns {Object} New settings fragment with randomized spaces
   */
  randomize(settings = {}, isFull = false, optionsList = PINTS_OPTIONS_DEFS) {
    const updated = { ...settings };
    const usedHoles = new Set();

    optionsList.forEach(opt => {
      const enable = isFull ? true : Math.random() < 0.65;
      updated[opt.chkId] = enable;
      if (!enable) return;

      const count = Math.floor(Math.random() * 2) + 1; // 1 to 2
      const pegs = 1;
      updated[opt.key + 'Count'] = count;
      updated[opt.key + 'Pegs'] = pegs;

      const holes = [];
      for (let i = 0; i < count; i++) {
        let attempts = 0;
        let h = 15;
        while (attempts < 50) {
          h = Math.floor(Math.random() * 110) + 5; // 5 to 115
          if (!usedHoles.has(h) && !usedHoles.has(h - 1) && !usedHoles.has(h + 1)) break;
          attempts++;
        }
        usedHoles.add(h);
        holes.push(h);
      }
      updated[opt.key + 'Holes'] = holes;
    });

    return updated;
  }

  /**
   * Resets all Pints spaces to unselected state.
   * @param {Array} [optionsList]
   * @returns {Object} Settings fragment with all options disabled
   */
  clearAll(optionsList = PINTS_OPTIONS_DEFS) {
    const cleared = {};
    optionsList.forEach(opt => {
      cleared[opt.chkId] = false;
      cleared[opt.key] = false;
    });
    return cleared;
  }

  triggerBonusModal(player, holeNum, bonusDef, continuationCb) {
    const overlay = typeof document !== 'undefined' ? document.getElementById('pintsBonusOverlay') : null;
    const titleEl = typeof document !== 'undefined' ? document.getElementById('pintsBonusTitle') : null;
    const playerEl = typeof document !== 'undefined' ? document.getElementById('pintsBonusPlayer') : null;
    const iconEl = typeof document !== 'undefined' ? document.getElementById('pintsBonusIcon') : null;
    const msgEl = typeof document !== 'undefined' ? document.getElementById('pintsBonusMessage') : null;

    if (iconEl) iconEl.textContent = bonusDef.icon || '🍺';
    if (titleEl) titleEl.textContent = `${bonusDef.icon ? bonusDef.icon + ' ' : ''}${bonusDef.name}`;
    if (playerEl) playerEl.textContent = `${player.name || player.label || 'Player'} landed on Hole ${holeNum}!`;
    if (msgEl) msgEl.textContent = bonusDef.description || bonusDef.actionText || bonusDef.name;

    if (overlay) {
      overlay.style.display = 'flex';
    }

    return continuationCb;
  }

  /**
   * Displays the Pints & Pegs drinking bonus modal dialog and pauses match flow.
   * @param {Object} optionDef
   * @param {string} playerLabel
   * @param {number} holeNum
   * @param {Object} [context]
   */
  static showPintsBonusModal(optionDef, playerLabel, holeNum, context = {}) {
    const state = context.state || (typeof window !== 'undefined' ? window.state : null);
    const log = context.log || (typeof window !== 'undefined' ? window.log : () => {});
    const setStatus = context.setStatus || (typeof window !== 'undefined' ? window.setStatus : () => {});
    const sfx = context.sfx || (typeof window !== 'undefined' ? window.sfx : null);
    const pintsManager = context.pintsManager || (typeof window !== 'undefined' ? window.pintsManager : null);

    if (state) {
      state.pausedForPintsBonus = true;
      state.paused = true;
      const desc = optionDef.description || optionDef.actionText || optionDef.name;
      log(`🍺 PINTS BONUS: ${playerLabel || 'Player'} landed on Hole ${holeNum} (${optionDef.name}) — ${desc}! Game is paused.`);
      setStatus(`Landed on Hole ${holeNum} (${optionDef.name})! Game is paused.`);
    }

    if (pintsManager && typeof pintsManager.triggerBonusModal === 'function') {
      pintsManager.triggerBonusModal({ name: playerLabel }, holeNum, optionDef);
    }

    if (sfx && typeof sfx.snap === 'function') sfx.snap();
  }

  /**
   * Closes the Pints drinking modal and resumes gameplay.
   * @param {Object} [context]
   */
  static closePintsBonusModal(context = {}) {
    const pintsManager = context.pintsManager || (typeof window !== 'undefined' ? window.pintsManager : null);
    const hideModal = context.hideModal || (typeof window !== 'undefined' ? window.hideModal : () => {});
    const resumeGameAfterPintsBonus = context.resumeGameAfterPintsBonus || (typeof window !== 'undefined' ? window.resumeGameAfterPintsBonus : PintsManager.resumeGameAfterPintsBonus);

    if (pintsManager && typeof pintsManager.closeModal === 'function') {
      pintsManager.closeModal();
    }
    if (typeof document !== 'undefined') {
      const overlay = document.getElementById('pintsBonusOverlay');
      if (overlay) overlay.style.display = 'none';
      else hideModal();
    } else {
      hideModal();
    }

    resumeGameAfterPintsBonus(context);
  }

  /**
   * Unpauses match and resumes play or turn scheduling after Pints bonus dismissal.
   * @param {Object} [context]
   */
  static resumeGameAfterPintsBonus(context = {}) {
    const state = context.state || (typeof window !== 'undefined' ? window.state : null);
    if (!state) return;
    const isSeatCpu = context.isSeatCpu || (typeof window !== 'undefined' ? window.isSeatCpu : () => false);
    const scheduleOppPlay = context.scheduleOppPlay || (typeof window !== 'undefined' ? window.scheduleOppPlay : () => {});
    const render = context.render || (typeof window !== 'undefined' ? window.render : () => {});

    state.pausedForPintsBonus = false;
    if (!state.pausedForSign && !state.pausedForAdultTimer && !state.pausedForAdultStrip) {
      state.paused = false;
    }
    if (typeof state._resumeAfterPintsBonus === 'function') {
      const resumeFn = state._resumeAfterPintsBonus;
      state._resumeAfterPintsBonus = null;
      state._resumeAfterAdultTimer = null;
      state._resumeAfterSign = null;
      resumeFn();
    } else if (typeof state._resumeAfterSign === 'function' && !state.pausedForSign && !state.pausedForAdultTimer && !state.pausedForAdultStrip) {
      const resumeFn = state._resumeAfterSign;
      state._resumeAfterPintsBonus = null;
      state._resumeAfterAdultTimer = null;
      state._resumeAfterSign = null;
      resumeFn();
    } else if (typeof state._resumeAfterAdultTimer === 'function' && !state.pausedForAdultTimer && !state.pausedForAdultStrip && !state.pausedForSign) {
      const resumeFn = state._resumeAfterAdultTimer;
      state._resumeAfterPintsBonus = null;
      state._resumeAfterAdultTimer = null;
      state._resumeAfterSign = null;
      resumeFn();
    } else if (state.phase === 'play' && isSeatCpu(state.turn) && !state.paused) {
      scheduleOppPlay(state.turn, context);
    } else if (state.phase === 'play' && !state.paused) {
      render();
    }
  }

  showPintsBonusModal(optionDef, playerLabel, holeNum, context) {
    return PintsManager.showPintsBonusModal(optionDef, playerLabel, holeNum, context);
  }

  closePintsBonusModal(context) {
    return PintsManager.closePintsBonusModal(context);
  }

  resumeGameAfterPintsBonus(context) {
    return PintsManager.resumeGameAfterPintsBonus(context);
  }
}
